import React, {
  useEffect,
  useState,
  useMemo,
  useContext,
  useCallback,
  useRef,
} from "react";
import CollapsibleTable from "components/CollapsibleTable";
import { Box, Typography, Grid, IconButton } from "@mui/material";
import {
  usePoliciesForReview,
  useExportAdoptionHistory,
} from "./usePoliciesReview";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import moment from "moment";
import { XlsxColumn, exportExcelWb } from "utils/excel";
import { Button, ButtonGroup } from "@mui/material";
import ImportVariance from "containers/PolicyHistory/Component/ImportVariance";
import TopPanelComponent from "./Component/TopPanelComponent";
import HistoryModal from "./Component/HistoryModal";
import HealthPlanSelect from "components/common/HealthPlanSelect";
import { useSelector } from "react-redux";
import QuickFilters from "./Component/Filter/QuickFilter";
import useGridResizeHook, {
  calculateGridHeight,
} from "hooks/useGridResizeHook";

const filterStorageKey = "policyAdoptionHistoryFilter";

const updateLocalStorageForFilter = (filterType, value) => {
  const filters = JSON.parse(sessionStorage.getItem(filterStorageKey)) || {};
  filters[filterType] = value;
  sessionStorage.setItem(filterStorageKey, JSON.stringify(filters));
};

const formatQuarter = (item) =>
  `${String(item.year).slice(-2)} ${item.quarter}`;

const matchesAdoptionFilter = (decision, code) =>
  decision?.adoptionDecisionStatusCode === code ||
  decision?.adoptionDecisionStatusDesc === code;

function PolicyHistory() {
  const topPanelRef = useRef(null);
  const [gridHeight, setGridHeight] = useState("auto");
  const hp = useSelector((state) => state.healthPlan.healthPlan);
  const parsedFilters = JSON.parse(sessionStorage.getItem(filterStorageKey));
  const selectedHP = hp?.healthPlanKey;
  const [quarterActiveFilter, setQuarterActiveFilter] = useState(
    parsedFilters?.quarter || ""
  );
  const [adoptedActiveFilter, setAdoptedActiveFilter] = useState(
    parsedFilters?.adoption || ""
  );
  const [historyModal, sethistoryModal] = useState(false);
  const [historyModalData, setHistoryModalData] = useState([]);
  const [historyPolicyData, setHistoryPolicyData] = useState([]);
  const [fetchPoliciesForReview, policiesForReview] =
    usePoliciesForReview(selectedHP);
  const [exportAdoptionHistory] = useExportAdoptionHistory(selectedHP);
  const getRowId = (row) => row.policyKey;

  const activeQuarter = useMemo(() => moment().quarter(), []);
  const activeYear = useMemo(() => {
    const y = moment().year();
    if (!quarterActiveFilter) return y;
    if (activeQuarter === 1 && quarterActiveFilter === "Q4") return y - 1;
    if (activeQuarter === 4 && quarterActiveFilter === "Q1") return y + 1;
    return y;
  }, [quarterActiveFilter, activeQuarter]);

  const columns = [
    { field: "policyNumber", headerName: "Policy Number", flex: 1 },
    {
      field: "policyTitle",
      headerName: "Policy Name",
      flex: 6,
    },
    { field: "quarter", headerName: "Latest CAB", flex: 1 },
    {
      field: "adoptionDecision",
      headerName: "Latest Adoption Decision",
      flex: 2,
    },
    {
      field: "historyLink",
      headerName: "History Link",
      flex: 2,
      renderCell: (params) => {
        return (
          <Box>
            <IconButton
              aria-label="View History"
              onClick={() => {
                openHistoryModal(params.row);
              }}
              size="small"
            >
              <Box component="span" title="View History">
                <WorkHistoryIcon />
              </Box>
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const exportAllAdoptionHistory = () => {
    exportAdoptionHistory();
  };

  const closeHistoryModal = (event, reason) => {
    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
      setHistoryModalData([]);
      sethistoryModal(false);
    }
  };

  const openHistoryModal = (data) => {
    setHistoryPolicyData(adoptedDetailsFilter);
    setHistoryModalData(data);
    sethistoryModal(true);
  };

  const exportTable = () => {
    const columns = [
      new XlsxColumn("Policy Number", "policyNumber", 10),
      new XlsxColumn("Latest CAB", "quarter", 10),
      new XlsxColumn(
        "Latest Adoption Decision",
        "adoptionDecisionStatusDesc",
        30
      ),
      new XlsxColumn("Policy Name", "policyTitle", 10),
    ];

    const rows = consolidatedAdoptedDetailsFilter.map((policy) => ({
      policyNumber: policy.policyNumber,
      policyTitle: policy.policyTitle,
      quarter: policy.quarter,
      adoptionDecisionStatusDesc: policy.adoptionDecision,
    }));

    exportExcelWb("policiesForReview", columns, rows);
  };

  const resetFilter = () => {
    setQuarterActiveFilter("");
    setAdoptedActiveFilter("");
    sessionStorage.removeItem(filterStorageKey);
  };

  const policiesForReviewData = useMemo(() => {
    if (!quarterActiveFilter && !adoptedActiveFilter) return policiesForReview;

    return policiesForReview.filter((item) => {
      const quarterCondition =
        !quarterActiveFilter ||
        (item.quarter === quarterActiveFilter && item.year === activeYear);
      const adoptedCondition =
        !adoptedActiveFilter ||
        item.clientAdoptionDecisions.some(
          (decision) =>
            decision.adoptionDecisionStatusCode === adoptedActiveFilter ||
            decision.adoptionDecisionStatusDesc === adoptedActiveFilter
        );

      return quarterCondition && adoptedCondition;
    });
  }, [quarterActiveFilter, policiesForReview, adoptedActiveFilter, activeYear]);

  const adoptedDetailsFilter = (policiesForReviewData ?? []).map((item) => {
    const filteredDecisions = adoptedActiveFilter
      ? (item.clientAdoptionDecisions ?? []).filter((d) =>
          matchesAdoptionFilter(d, adoptedActiveFilter)
        )
      : (item.clientAdoptionDecisions ?? []);

    const first = filteredDecisions[0];

    return {
      ...item,
      quarter: formatQuarter(item),
      clientAdoptionDecisions: filteredDecisions,
      adoptionDecision:
        first?.adoptionDecisionStatusDesc ??
        first?.adoptionDecisionStatusCode ??
        undefined,
    };
  });

  const filteredAdoptedDetails = adoptedDetailsFilter.filter(
    (item) =>
      Array.isArray(item.clientAdoptionDecisions) &&
      item.clientAdoptionDecisions.length > 0
  );

  // Consolidate adoptedDetailsFilter by policyNumber, keeping the latest quarter/year
  const consolidatedAdoptedDetailsFilter = (() => {
    const rowData = new Map();

    filteredAdoptedDetails.forEach((item) => {
      const key = item.policyNumber;
      const prev = rowData.get(key);

      // Parse quarter and year for comparison
      let quarter = 0,
        year = 0;
      if (item.quarter && typeof item.quarter === "string") {
        const match = item.quarter.match(/Q([1-4])\s*(\d{2,4})?/);
        if (match) {
          quarter = parseInt(match[1], 10);
          year =
            item.year ||
            (match[2]
              ? match[2].length === 2
                ? 2000 + parseInt(match[2], 10)
                : parseInt(match[2], 10)
              : 0);
        } else {
          quarter = 0;
          year = item.year || 0;
        }
      } else {
        quarter = 0;
        year = item.year || 0;
      }

      // If no previous or this is newer, replace
      if (!prev) {
        rowData.set(key, { ...item, _quarter: quarter, _year: year });
      } else {
        const prevQuarter = prev._quarter ?? 0;
        const prevYear = prev._year ?? 0;
        if (year > prevYear || (year === prevYear && quarter > prevQuarter)) {
          rowData.set(key, { ...item, _quarter: quarter, _year: year });
        }
      }
    });

    // Remove helper fields before returning
    return Array.from(rowData.values()).map(
      ({ _quarter, _year, ...rest }) => rest
    );
  })();

  const exportComponent = selectedHP ? (
    <>
      <ButtonGroup variant="text">
        <ImportVariance
          hpKey={selectedHP}
          refreshData={() => fetchPoliciesForReview(selectedHP)}
        />
        <Button onClick={exportTable}>Export</Button>
        <Button onClick={exportAllAdoptionHistory}>Export All</Button>
      </ButtonGroup>
    </>
  ) : (
    ""
  );
  const resizeEvent = useCallback(
    () => calculateGridHeight(topPanelRef, setGridHeight),
    [setGridHeight]
  );
  useGridResizeHook(resizeEvent);

  const handleAccordionToggle = () => {
    setTimeout(() => {
      resizeEvent();
    }, 400);
  };

  return (
    <div>
      <Box ref={topPanelRef}>
        <TopPanelComponent />
        <Box sx={{ width: 0.5 }} display="flex">
          <Typography
            variant="h2"
            component="h2"
            sx={{
              width: 225,
              alignSelf: "center",
              fontSize: 18,
              fontWeight: "bold",
            }}
          >
            Health Plan
          </Typography>
          <HealthPlanSelect />
        </Box>
        <Box sx={{ py: 1 }}>
          <QuickFilters
            handleAccordionToggle={handleAccordionToggle}
            activeQuarter={activeQuarter}
            quarterActiveFilter={quarterActiveFilter}
            setQuarterActiveFilter={setQuarterActiveFilter}
            adoptedActiveFilter={adoptedActiveFilter}
            setAdoptedActiveFilter={setAdoptedActiveFilter}
            updateLocalStorageForFilter={updateLocalStorageForFilter}
            resetFilter={resetFilter}
          />
        </Box>
      </Box>
      <HistoryModal
        open={historyModal}
        handleClose={closeHistoryModal}
        historyModalData={historyModalData}
        historyPolicyData={historyPolicyData}
      />
      <Box sx={{ display: "flex", height: `${gridHeight}px !important ` }}>
        <CollapsibleTable
          initialState={{
            sorting: {
              sortModel: [{ field: "policyNumber", sort: "asc" }],
            },
          }}
          getRowId={getRowId}
          rows={consolidatedAdoptedDetailsFilter}
          columns={columns}
          error={{}}
          hpKey={selectedHP}
          exportComponent={exportComponent}
        />
      </Box>
    </div>
  );
}

export default PolicyHistory;
